/* ==========================================================================
   T.P. Mills Putter Finder - result view (Launch Candidate)

   Split out of assets/putter-finder.js so each asset stays small enough to
   upload and verify reliably, and so the customer-facing result surface can be
   reviewed on its own.

   Contract: this file publishes window.PutterFinderResult.create(ctx) and
   returns { render }. Everything it needs is passed in through ctx - it reads
   no globals of its own except document/window/fetch. That keeps the question
   flow and the result surface independently testable.

   ctx = {
     app, dataset, state, questions, scoring, debug,
     el, track, announce, focusHeading, renderNav, renderStatus,
   }
   ========================================================================== */
(function (global) {
  'use strict';

  function create(ctx) {
    var app = ctx.app;
    var dataset = ctx.dataset;
    var state = ctx.state;
    var QUESTIONS = ctx.questions;
    var SCORING = ctx.scoring;
    var PF_DEBUG = ctx.debug;
    var el = ctx.el;
    var track = ctx.track;
    var announce = ctx.announce;
    var focusHeading = ctx.focusHeading;
    var renderNav = ctx.renderNav;
    var renderStatus = ctx.renderStatus;

  /* ================================================== Launch Candidate ======
     Customer-facing result card, live product navigation, Add to cart for
     stocked lines, and optional email / inquiry sections.

     Rules that are enforced structurally rather than by convention:
       - the raw fit percentage is never rendered;
       - Add to cart exists only for club_champ / tour_issue, only on a scored
         result, and only when one exact variant can be identified;
       - the Starting Direction path has no cart control at all;
       - nothing personal is written to storage. Submitted values live only in
         the form element until the browser posts them. */

  var UTM = 'utm_source=putter_finder&utm_medium=result&utm_campaign=find_my_putter';
  var FALLBACK_URL = '/collections/all';
  var HANDMADE_COLLECTION_URL = '/collections/all-putters';
  var CONTACT_EMAIL = 'tpmills@tpmills.com';
  var HAND_NOTE = 'Current online models are listed in right-handed configurations. ' +
    'Please review product availability before purchase.';
  var INQUIRY_NOTE = 'One-of-one and handmade putters may require availability confirmation.';
  var LENGTH_UNAVAILABLE_NOTE = 'Selected length is unavailable. ' +
    'Please view the product page to review available options.';
  var MULTI_OPTION_NOTE = 'This model has additional options to choose. ' +
    'Please view the product page to select them.';

  var CART_TIERS = ['club_champ', 'tour_issue'];
  /* SOLANA-style products have colour AND length, so a length alone does not
     identify one variant. Adding to cart would silently pick a colour for the
     customer. Flip to true only if you would rather ship a default colour than
     ask for one more click. */
  var ALLOW_MULTI_OPTION_CART = false;
  var LENGTH_FROM_ANSWER = { len_33: '33', len_34: '34', len_35: '35' };
  var DEFAULT_LENGTH = '34';

  var urlWarnings = [];

  /* ------------------------------------------------------------- helpers --- */

  function productHref(product, content) {
    var base = product && product.url ? String(product.url) : '';
    if (!base) {
      urlWarnings.push(product && product.handle ? product.handle : '(unknown handle)');
      base = FALLBACK_URL;
    }
    return base + (base.indexOf('?') === -1 ? '?' : '&') + UTM + '&utm_content=' + content;
  }

  function meaningfulCount() {
    return SCORING ? SCORING.lowInformation(state.answers).meaningfulCount : null;
  }

  function basePayload() {
    return {
      finder: 'putter_finder',
      low_information: !!(state.lastResult && state.lastResult.lowInformation),
      match_label: state.lastLabel ? state.lastLabel.key : null,
      answered_count: Object.keys(state.answers).length,
      meaningful_count: meaningfulCount(),
    };
  }

  function viewButton(entry, content, eventName, label) {
    var p = entry.product;
    var a = el('a', 'putter-finder__btn putter-finder__btn--secondary', label || 'View product');
    a.href = productHref(p, content);
    a.setAttribute('data-pf-handle', p.handle);
    a.addEventListener('click', function () {
      track(eventName, Object.assign(basePayload(), {
        clicked_handle: p.handle,
        utm_content: content,
      }));
    });
    return a;
  }

  /* Length parsing deliberately recognises 33/34/35 only. 32-inch variants are
     never selected and never presented, per owner decision. */
  function parseLength(variantTitle) {
    var m = String(variantTitle).match(/\b(3[3-5])\b/) || String(variantTitle).match(/(3[3-5])\s*(?:inch|in\b|")/i);
    return m ? m[1] : null;
  }

  function optionCount(variantTitle) {
    return String(variantTitle).split(' / ').length;
  }

  function lengthText(product) {
    var seen = [];
    (product.variants || []).forEach(function (v) {
      if (!v.available) return;
      var L = parseLength(v.title);
      if (L && seen.indexOf(L) === -1) seen.push(L);
    });
    seen.sort();
    if (seen.length) return seen.map(function (n) { return n + '"'; }).join(' \u00b7 ');
    if ((product.variants || []).length === 1 && /default/i.test(String(product.variants[0].title))) {
      return 'Made to order \u2014 length confirmed before shipping';
    }
    return 'Length availability to be confirmed';
  }

  function priceText(product) {
    if (product.price_formatted) return product.price_formatted;
    if (typeof product.price === 'number') return '$' + (product.price / 100).toFixed(2);
    return '';
  }

  function media(product) {
    var box = el('div', 'putter-finder__media');
    if (product.image) {
      var img = document.createElement('img');
      img.src = product.image;
      img.alt = product.title;
      img.loading = 'lazy';
      img.decoding = 'async';
      box.appendChild(img);
    } else {
      box.appendChild(el('span', 'putter-finder__media-empty', 'No image'));
    }
    return box;
  }

  function isInquiry(product) {
    return product.tier === 'handmade' || product.tier === 'sgss';
  }

  /* ------------------------------------------------------ cart eligibility ---
     Returns a decision object, never throws, and always explains itself so the
     reason can be shown to the customer and logged for debugging. */
  function cartDecision(scored) {
    var p = scored.product;

    if (CART_TIERS.indexOf(p.tier) === -1) {
      return { allowed: false, code: 'tier_not_eligible', variant: null, length: null };
    }

    var available = (p.variants || []).filter(function (v) { return v.available; });
    if (!available.length) {
      return { allowed: false, code: 'no_available_variant', variant: null, length: null };
    }

    var answer = state.answers.length_preference;
    var wanted = answer ? (LENGTH_FROM_ANSWER[answer.value] || null) : null;
    var explicit = wanted !== null;

    /* Never offer a 32-inch variant, whatever the customer answered. */
    var candidates = available.filter(function (v) {
      return !/\b32\b/.test(String(v.title));
    });
    if (!candidates.length) {
      return { allowed: false, code: 'no_available_variant', variant: null, length: null };
    }

    var match = null;
    var chosenLength = null;

    if (explicit) {
      match = candidates.filter(function (v) { return parseLength(v.title) === wanted; });
      chosenLength = wanted;
      if (!match.length) {
        return { allowed: false, code: 'length_unavailable', variant: null, length: wanted };
      }
    } else {
      match = candidates.filter(function (v) { return parseLength(v.title) === DEFAULT_LENGTH; });
      chosenLength = DEFAULT_LENGTH;
      if (!match.length) {
        match = candidates.slice(0, 1);
        chosenLength = parseLength(match[0].title);
      }
    }

    if (match.length > 1 || optionCount(match[0].title) > 1) {
      if (!ALLOW_MULTI_OPTION_CART) {
        return { allowed: false, code: 'multi_option', variant: null, length: chosenLength };
      }
    }

    return { allowed: true, code: 'ok', variant: match[0], length: chosenLength };
  }

  function addToCartControl(scored, decision) {
    var wrap = el('div', 'putter-finder__cart');
    var row = el('div', 'putter-finder__ctas');

    var btn = el('button', 'putter-finder__btn putter-finder__btn--primary', 'Add recommended length to cart');
    btn.type = 'button';
    row.appendChild(btn);
    row.appendChild(viewButton(scored, 'best_match', 'finder_product_clicked'));
    wrap.appendChild(row);

    var chosen = el('p', 'putter-finder__cart-note',
      'Adding: ' + decision.variant.title + ' \u00b7 quantity 1');
    wrap.appendChild(chosen);

    var error = el('p', 'putter-finder__cart-error');
    error.hidden = true;
    wrap.appendChild(error);

    btn.addEventListener('click', function () {
      track('finder_add_to_cart_clicked', Object.assign(basePayload(), {
        clicked_handle: scored.product.handle,
        best_match_handle: scored.product.handle,
        variant_id: decision.variant.id,
        selected_length: decision.length,
      }));

      btn.disabled = true;
      btn.textContent = 'Adding\u2026';
      error.hidden = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: decision.variant.id, quantity: 1 }] }),
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (b) { throw new Error(b && b.description ? b.description : 'Cart returned ' + res.status); });
          return res.json();
        })
        .then(function () {
          track('finder_add_to_cart_success', Object.assign(basePayload(), {
            clicked_handle: scored.product.handle,
            best_match_handle: scored.product.handle,
            variant_id: decision.variant.id,
            selected_length: decision.length,
          }));
          window.location.href = '/cart';
        })
        .catch(function (err) {
          track('finder_add_to_cart_error', Object.assign(basePayload(), {
            clicked_handle: scored.product.handle,
            variant_id: decision.variant.id,
            selected_length: decision.length,
            error_message: String(err && err.message ? err.message : err),
          }));
          btn.disabled = false;
          btn.textContent = 'Add recommended length to cart';
          error.hidden = false;
          error.textContent = 'We could not add this to your cart. Please use View product to continue.';
          announce('Add to cart failed. Please use the view product link.');
        });
    });

    return wrap;
  }

  function noCartControl(scored, decision) {
    var wrap = el('div', 'putter-finder__cart');
    var row = el('div', 'putter-finder__ctas');
    row.appendChild(viewButton(scored, 'best_match', 'finder_product_clicked'));
    wrap.appendChild(row);

    if (decision.code === 'length_unavailable') {
      wrap.appendChild(el('p', 'putter-finder__cart-note', LENGTH_UNAVAILABLE_NOTE));
    } else if (decision.code === 'multi_option') {
      wrap.appendChild(el('p', 'putter-finder__cart-note', MULTI_OPTION_NOTE));
    } else if (decision.code === 'no_available_variant') {
      wrap.appendChild(el('p', 'putter-finder__cart-note',
        'This putter is not currently available online. Please view the product page.'));
    }
    return wrap;
  }

  /* ---------------------------------------------------------------- cards --- */

  function bestCard(scored, labelInfo, decision) {
    var p = scored.product;
    var card = el('article', 'putter-finder__card putter-finder__card--best');
    card.appendChild(media(p));

    var body = el('div', 'putter-finder__card-body');
    var head = el('div', 'putter-finder__card-head');
    head.appendChild(el('p', 'putter-finder__card-rank', 'Best Match'));
    head.appendChild(el('p', 'putter-finder__match-label', labelInfo.label));
    body.appendChild(head);

    body.appendChild(el('h3', 'putter-finder__card-title', p.title));
    if (labelInfo.note) body.appendChild(el('p', 'putter-finder__match-note', labelInfo.note));
    body.appendChild(el('p', 'putter-finder__card-price', priceText(p)));

    var specs = el('dl', 'putter-finder__specs');
    specs.appendChild(el('dt', null, 'Available lengths'));
    specs.appendChild(el('dd', null, lengthText(p)));
    body.appendChild(specs);

    if (p.fit_notes) {
      body.appendChild(el('p', 'putter-finder__card-notes',
        String(p.fit_notes).split('. ')[0].replace(/\.$/, '') + '.'));
    }

    body.appendChild(el('p', 'putter-finder__card-why', 'Why this fits'));
    var ul = el('ul', 'putter-finder__why-list');
    var bullets = SCORING.reasonsFor(scored, 3);
    bullets.forEach(function (b) { ul.appendChild(el('li', null, b)); });
    if (!bullets.length) ul.appendChild(el('li', null, 'Selected as the closest available profile for your answers.'));
    body.appendChild(ul);

    if (isInquiry(p) || scored.inquiryOnly) {
      var row = el('div', 'putter-finder__ctas');
      row.appendChild(viewButton(scored, 'best_match', 'finder_product_clicked'));
      body.appendChild(row);
      body.appendChild(el('p', 'putter-finder__inquiry-note', INQUIRY_NOTE));
      var inquiry = handmadeInquirySection(p);
      if (inquiry) body.appendChild(inquiry);
    } else if (decision.allowed) {
      body.appendChild(addToCartControl(scored, decision));
    } else {
      body.appendChild(noCartControl(scored, decision));
    }

    card.appendChild(body);
    return card;
  }

  function altCard(scored, index) {
    var p = scored.product;
    var card = el('article', 'putter-finder__card putter-finder__card--alt');
    card.appendChild(media(p));

    var body = el('div', 'putter-finder__card-body');
    body.appendChild(el('p', 'putter-finder__card-rank', 'Alternative ' + (index + 1)));
    body.appendChild(el('h3', 'putter-finder__card-title', p.title));
    body.appendChild(el('p', 'putter-finder__card-price', priceText(p)));

    var reason = SCORING.reasonsFor(scored, 1)[0] ||
      (p.fit_notes ? String(p.fit_notes).split('. ')[0] + '.' : 'A different direction worth comparing.');
    body.appendChild(el('p', 'putter-finder__card-reason', reason));

    var ctas = el('div', 'putter-finder__ctas');
    ctas.appendChild(viewButton(scored, 'alternative_' + (index + 1), 'finder_alternative_clicked'));
    body.appendChild(ctas);

    if (isInquiry(p) || scored.inquiryOnly) {
      body.appendChild(el('p', 'putter-finder__inquiry-note', INQUIRY_NOTE));
    }

    card.appendChild(body);
    return card;
  }

  /* -------------------------------------------------------- compare panel --- */

  var COMPARE_ROWS = [
    ['Head shape', 'head_shape'],
    ['Balance', 'balance'],
    ['Alignment', 'alignment'],
    ['Material', 'material'],
    ['Line', 'tier'],
  ];

  function humanise(v) {
    if (v === null || v === undefined || v === '') return '\u2014';
    return String(v).replace(/_/g, ' ');
  }

  function disclosure(labelClosed, labelOpen, id, onOpen) {
    var wrap = el('section', 'putter-finder__panel');
    var btn = el('button', 'putter-finder__btn putter-finder__btn--ghost', labelClosed);
    btn.type = 'button';
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', id);
    var body = el('div', 'putter-finder__panel-body');
    body.id = id;
    body.hidden = true;
    var opened = false;
    btn.addEventListener('click', function () {
      var isOpen = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      body.hidden = isOpen;
      btn.textContent = isOpen ? labelClosed : labelOpen;
      if (!isOpen && !opened) { opened = true; if (onOpen) onOpen(); }
      announce(isOpen ? 'Section collapsed.' : 'Section expanded.');
    });
    wrap.appendChild(btn);
    wrap.appendChild(body);
    wrap.body = body;
    return wrap;
  }

  function comparePanel(entries) {
    var panel = disclosure('Compare alternatives', 'Hide comparison', 'putter-finder-compare-body');
    panel.classList.add('putter-finder__compare');

    var table = el('table', 'putter-finder__compare-table');
    var thead = el('thead');
    var hr = el('tr');
    hr.appendChild(el('th', null, ''));
    entries.forEach(function (e) {
      var th = el('th', null, e.product.title);
      th.setAttribute('scope', 'col');
      hr.appendChild(th);
    });
    thead.appendChild(hr);
    table.appendChild(thead);

    var tbody = el('tbody');
    COMPARE_ROWS.forEach(function (row) {
      var tr = el('tr');
      var th = el('th', null, row[0]);
      th.setAttribute('scope', 'row');
      tr.appendChild(th);
      entries.forEach(function (e) { tr.appendChild(el('td', null, humanise(e.product[row[1]]))); });
      tbody.appendChild(tr);
    });
    var lr = el('tr');
    var lth = el('th', null, 'Available lengths');
    lth.setAttribute('scope', 'row');
    lr.appendChild(lth);
    entries.forEach(function (e) { lr.appendChild(el('td', null, lengthText(e.product))); });
    tbody.appendChild(lr);
    table.appendChild(tbody);

    var scroller = el('div', 'putter-finder__compare-scroll');
    scroller.appendChild(table);
    panel.body.appendChild(scroller);
    return panel;
  }

  /* ------------------------------------------------- lead capture forms ---
     Provided by assets/putter-finder-forms.js. If that asset fails to load the
     result still renders in full; only the optional panels are omitted. */
  var FORMS = (typeof global !== 'undefined' && global.PutterFinderForms) ||
    (typeof window !== 'undefined' && window.PutterFinderForms) || null;
  var forms = FORMS ? FORMS.create({
    state: state,
    questions: QUESTIONS,
    el: el,
    track: track,
    basePayload: basePayload,
    disclosure: disclosure,
    meaningfulCount: meaningfulCount,
    contactEmail: CONTACT_EMAIL,
  }) : null;

  function emailResultSection(ranked, labelInfo, profile, decision) {
    return forms ? forms.emailResultSection(ranked, labelInfo, profile, decision) : null;
  }

  function handmadeInquirySection(product) {
    return forms ? forms.handmadeInquirySection(product) : null;
  }

  /* ------------------------------------------------------------- chrome --- */

  function handNote() {
    return el('p', 'putter-finder__hand-note', HAND_NOTE);
  }

  function devWarnings(host) {
    if (urlWarnings.length) {
      renderStatus(host, {
        warn: true,
        title: 'Development warning',
        lines: ['These products have no product.url in the dataset and fall back to ' + FALLBACK_URL + ':'],
        items: urlWarnings.slice(),
      });
    }
  }

  function customerFallback(host, line) {
    renderStatus(host, {
      warn: false,
      customer: true,
      title: 'We could not complete your match',
      lines: [line, 'You can browse the full T.P. Mills putter range instead.'],
    });
    var a = el('a', 'putter-finder__btn putter-finder__btn--primary', 'Browse all putters');
    a.href = HANDMADE_COLLECTION_URL;
    var row = el('div', 'putter-finder__ctas');
    row.appendChild(a);
    host.appendChild(row);
  }

  function debugBanner(host, extra) {
    if (!PF_DEBUG) return;
    renderStatus(host, {
      warn: false,
      title: 'Debug status',
      lines: [
        'finder_debug=1 is active. These boxes are hidden without it.',
        'Dataset loaded: ' + (dataset ? dataset.count : 0) + ' products',
      ].concat(extra || []),
    });
  }

  /* --------------------------------------------- low-information result --- */

  function renderDirection(ranked) {
    var dir = ranked.direction;
    var info = ranked.lowInformationDetail;
    urlWarnings = [];

    app.appendChild(el('p', 'putter-finder__eyebrow', 'T.P. Mills Putter Finder'));

    var h = el('h2', 'putter-finder__heading', 'Your Starting Direction');
    h.setAttribute('tabindex', '-1');
    h.setAttribute('data-pf-focus', '');
    app.appendChild(h);

    var copy = el('div', 'putter-finder__direction-copy');
    (dir && dir.copy ? dir.copy : SCORING.DIRECTION_COPY).forEach(function (line) {
      copy.appendChild(el('p', null, line));
    });
    app.appendChild(copy);

    if (!dir) {
      renderStatus(app, { warn: true, title: 'Development warning', lines: ['No direction is mapped for this Step 1 answer.'] });
      customerFallback(app, 'We could not map your answer to a starting direction.');
      app.appendChild(handNote());
      app.appendChild(renderNav(true));
      return;
    }

    var label = el('p', 'putter-finder__direction-label', dir.label);
    if (dir.collectionLabel) {
      label.appendChild(el('span', 'putter-finder__direction-collection', ' \u2014 ' + dir.collectionLabel));
    }
    app.appendChild(label);

    var grid = el('div', 'putter-finder__grid');
    dir.products.forEach(function (entry, i) {
      var p = entry.product;
      var card = el('article', 'putter-finder__card putter-finder__card--direction');
      card.appendChild(media(p));
      var body = el('div', 'putter-finder__card-body');
      body.appendChild(el('h3', 'putter-finder__card-title', p.title));
      body.appendChild(el('p', 'putter-finder__card-price', priceText(p)));
      if (p.fit_notes) body.appendChild(el('p', 'putter-finder__card-reason', String(p.fit_notes).split('. ')[0] + '.'));
      var ctas = el('div', 'putter-finder__ctas');
      ctas.appendChild(viewButton(entry, 'direction_' + (i + 1), 'finder_product_clicked',
        entry.inquiryOnly ? 'View handmade collection' : 'View product'));
      body.appendChild(ctas);
      if (entry.inquiryOnly) body.appendChild(el('p', 'putter-finder__inquiry-note', INQUIRY_NOTE));
      card.appendChild(body);
      grid.appendChild(card);
    });
    app.appendChild(grid);

    if (!dir.products.length) {
      renderStatus(app, { warn: true, title: 'Development warning', lines: ['No products in the current dataset match this direction.'] });
      customerFallback(app, 'No putters are currently listed for this direction.');
    }

    app.appendChild(handNote());
    debugBanner(app, ['Trigger: ' + info.reasons.join(', ') + ' (active weight ' + info.activeWeight + ')']);
    devWarnings(app);
    if (dir.missingHandles.length) {
      renderStatus(app, {
        warn: true,
        title: 'Development warning',
        lines: ['Configured direction handles missing from dataset: ' + dir.missingHandles.join(', ')],
      });
    }
    app.appendChild(renderNav(true));

    track('finder_low_information_viewed', Object.assign(basePayload(), {
      result_type: 'starting_direction',
      result_profile: SCORING.profileFor(state.answers).name,
      alternative_handles: dir.products.map(function (e) { return e.product.handle; }),
      raw_score_internal: null,
      close_alternatives: false,
      selected_length: null,
      line_confidence_answer: dir.answer ? dir.answer.value : null,
      suggested_direction: dir.key,
    }));

    announce('Your Starting Direction is shown.');
    focusHeading();
  }

  /* ------------------------------------------------------- scored result --- */

  function renderResult() {
    /* the caller has already cleared the container */
    urlWarnings = [];

    if (!SCORING) {
      customerFallback(app, 'The matching engine did not load.');
      app.appendChild(renderNav(true));
      focusHeading();
      return;
    }

    var products = dataset ? dataset.products : [];
    var ranked = SCORING.rank(products, state.answers);
    var info = ranked.lowInformationDetail;
    var labelInfo = SCORING.matchLabel(ranked);
    state.lastResult = ranked;
    state.lastLabel = labelInfo;

    if (ranked.lowInformation) {
      state.lastSelection = null;
      renderDirection(ranked);
      return;
    }

    if (!ranked.best) {
      renderStatus(app, { warn: true, title: 'Development warning', lines: ['No products were available to score.'] });
      customerFallback(app, 'No putters were available to match against right now.');
      app.appendChild(handNote());
      app.appendChild(renderNav(true));
      return;
    }

    var profile = SCORING.profileFor(state.answers);
    var decision = cartDecision(ranked.best);
    state.lastSelection = decision;

    app.appendChild(el('p', 'putter-finder__eyebrow', 'T.P. Mills Putter Finder'));

    var h = el('h2', 'putter-finder__heading', 'Your Mills Match Profile');
    h.setAttribute('tabindex', '-1');
    h.setAttribute('data-pf-focus', '');
    app.appendChild(h);

    app.appendChild(el('p', 'putter-finder__profile-name', profile.name));
    if (profile.summary) app.appendChild(el('p', 'putter-finder__profile-summary', profile.summary));

    app.appendChild(bestCard(ranked.best, labelInfo, decision));

    var grid = el('div', 'putter-finder__grid');
    ranked.alternatives.forEach(function (alt, i) { grid.appendChild(altCard(alt, i)); });
    app.appendChild(grid);

    app.appendChild(comparePanel([ranked.best].concat(ranked.alternatives)));

    /* The result is fully rendered above before the email ask appears. */
    var emailPanel = emailResultSection(ranked, labelInfo, profile, decision);
    if (emailPanel) app.appendChild(emailPanel);

    app.appendChild(handNote());
    debugBanner(app, [
      'Internal score: ' + ranked.best.pct + ' | spread ' + labelInfo.spread + ' | cart ' + decision.code,
    ]);
    devWarnings(app);
    app.appendChild(renderNav(true));

    track('finder_result_viewed', Object.assign(basePayload(), {
      result_type: 'scored',
      result_profile: profile.name,
      best_match_handle: ranked.best.product.handle,
      alternative_handles: ranked.alternatives.map(function (a) { return a.product.handle; }),
      raw_score_internal: ranked.best.pct,
      close_alternatives: labelInfo.closeAlternatives,
      selected_length: decision.allowed ? decision.length : null,
      variant_id: decision.allowed ? decision.variant.id : null,
      add_to_cart_available: decision.allowed,
      add_to_cart_block_reason: decision.allowed ? null : decision.code,
    }));

    announce('Your Mills Match Profile is shown.');
    focusHeading();
  }

    return { render: renderResult };
  }

  global.PutterFinderResult = { create: create };
})(typeof window !== 'undefined' ? window : globalThis);
